import type { ChangeEvent, FunctionComponent } from 'react';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
import { Button } from '@trello/nachos/button';
import { TrashIcon } from '@trello/nachos/icons/trash';
import { Textfield } from '@trello/nachos/textfield';
import { Tooltip } from '@trello/nachos/tooltip';
import type { AppManagementTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { useUpdateAllowedOriginsMutation } from '../queries/UpdateAllowedOriginsMutation.generated';

import styles from './AllowedOrigins.less';

export const AllowedOrigins: FunctionComponent<{
  apiKey: string;
  origins: string[];
}> = ({ apiKey, origins }) => {
  const intl = useIntl();
  const [allowedOrigins, setAllowedOrigins] = useState<string[]>(origins);
  const [url, setUrl] = useState<string>('');
  const [isInvalidUrl, setIsInvalidUrl] = useState<boolean>(false);

  const onUrlChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setUrl(e.target.value);
    },
    [setUrl],
  );

  const taskName = 'edit-plugin/generateApiKey';
  const source = 'powerUpEditScreen';
  const [updateAllowedOrigins] = useUpdateAllowedOriginsMutation();

  const handleAddAllowedOrigin = useCallback(async () => {
    const urlExists = allowedOrigins.includes(url);
    if (urlExists) {
      setUrl('');
    } else if (
      url &&
      /^[a-z][a-z0-9+.-]*:/.test(url) &&
      !/[,&<>'"`\\]/.test(url)
    ) {
      const traceId = Analytics.startTask({
        taskName,
        source,
      });
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const origins = [...allowedOrigins, url];
      setIsInvalidUrl(false);
      try {
        await updateAllowedOrigins({
          variables: {
            keyApplication: apiKey,
            origins,
          },
        });
        setAllowedOrigins(origins);
        setUrl('');
        Analytics.taskSucceeded({
          taskName,
          traceId,
          source,
        });
      } catch (error) {
        Analytics.taskFailed({
          taskName,
          traceId,
          source,
          error,
        });
        sendErrorEvent(error, {
          tags: { ownershipArea: 'trello-web-eng' },
        });
      }
    } else {
      setIsInvalidUrl(true);
    }
  }, [
    url,
    allowedOrigins,
    apiKey,
    updateAllowedOrigins,
    setAllowedOrigins,
    setIsInvalidUrl,
  ]);

  const handleRemoveAllowedOrigin = useCallback(
    (key: number) => async () => {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const origins = allowedOrigins.filter((origin, idx) => idx !== key);
      const traceId = Analytics.startTask({
        taskName,
        source,
      });
      try {
        await updateAllowedOrigins({
          variables: {
            keyApplication: apiKey,
            origins,
          },
        });
        setAllowedOrigins(origins);
        setUrl('');
        Analytics.taskSucceeded({
          taskName,
          traceId,
          source,
        });
      } catch (error) {
        console.error(error);
        Analytics.taskFailed({
          taskName,
          traceId,
          source,
          error,
        });
        sendErrorEvent(error, {
          tags: { ownershipArea: 'trello-web-eng' },
        });
      }
    },
    [allowedOrigins, apiKey, updateAllowedOrigins],
  );

  return (
    <div>
      <div className={styles.formSection}>
        <div>
          <Textfield
            id="allowed-origins-input"
            className={styles.textfield}
            label={
              <label htmlFor="allowed-origins-input" className={styles.label}>
                <FormattedMessage
                  id="templates.app_management.allowed-origins"
                  defaultMessage="Allowed origins"
                  description="Label for the allowed origins input field"
                />
              </label>
            }
            placeholder={intl.formatMessage({
              id: 'templates.app_management.allowed-origin-example',
              defaultMessage: 'e.g. https://example.com',
              description:
                'Placeholder text for the allowed origins input field',
            })}
            value={url}
            onChange={onUrlChange}
            iconAfter={
              <Button
                className={styles.addBtn}
                children={
                  <FormattedMessage
                    id="templates.app_management.add"
                    defaultMessage="Add"
                    description="Button text for adding a new allowed origin"
                  />
                }
                onClick={handleAddAllowedOrigin}
              />
            }
          />
          {isInvalidUrl && (
            <span className={styles.isInvalidUrl}>
              <FormattedMessage
                id="templates.app_management.error-invalid-url"
                defaultMessage="This is not valid URL."
                description="Error message shown when an invalid URL is entered"
              />
            </span>
          )}
          <div className={styles.origins}>
            {allowedOrigins.map((value, idx) => (
              <div
                className={styles.origin}
                // eslint-disable-next-line @eslint-react/no-array-index-key -- FIXME
                key={idx}
                data-testid={getTestId<AppManagementTestIds>('allowed-origin')}
              >
                <div>{value}</div>
                <Tooltip
                  content={
                    <FormattedMessage
                      id="templates.app_management.delete-allowed-origin"
                      defaultMessage="Delete allowed origin"
                      description="Tooltip text for the delete allowed origin button"
                    />
                  }
                  position="top"
                >
                  <Button
                    className={styles.copyBtn}
                    children={<TrashIcon size="small" />}
                    onClick={handleRemoveAllowedOrigin(idx)}
                  />
                </Tooltip>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.inputDescription}>
          <FormattedMessage
            id="templates.app_management.allowed-origins-description"
            defaultMessage="You should specify the <a>origins</a> that your application is allowed to redirect back to following the authorization flow. Trello will prevent a redirect to any origin not specified here."
            description="Description text explaining what allowed origins are and how they work"
            values={{
              a: (chunks) => (
                <a href="https://developer.atlassian.com/cloud/trello/guides/rest-api/authorization/#allowed-origins">
                  {chunks}
                </a>
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
};
